/**
 * API модуля Estimation — Presale CRUD, файлы и прогоны (согласно Swagger /presale/*)
 */
import apiReq from 'core/api';
import type {
  Presale,
  PresaleListing,
  PresaleRun,
  PresaleCreateForm,
  PresaleUpdateForm,
  BitrixFieldMeta,
  PresaleBitrixValues,
  SubmitPresaleBitrixForm,
  PresalePrompt,
  PresalePromptUpdateForm,
} from './types';

// ─── Presale CRUD ─────────────────────────────────────────────────────────────

/** GET /presales — Список пресейлов проекта */
export const getPresales = async (projectId: string): Promise<PresaleListing[]> => {
  const params = new URLSearchParams({ project_id: projectId });
  const data = await apiReq<{ count: number; items: PresaleListing[] }>(`presales?${params}`);
  return data?.items ?? [];
};

/** POST /presale/create — Создать пресейл */
export const createPresale = async (form: PresaleCreateForm): Promise<Presale> => {
  const data = await apiReq<Presale>('presale/create', 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

/** GET /presale/{id}/view — Получить пресейл */
export const getPresale = async (id: string): Promise<Presale> => {
  const data = await apiReq<Presale>(`presale/${id}/view`);
  if (!data) throw new Error('Presale not found');
  return data;
};

/** POST /presale/{id}/update — Обновить поля пресейла */
export const updatePresale = async (id: string, form: PresaleUpdateForm): Promise<Presale> => {
  const data = await apiReq<Presale>(`presale/${id}/update`, 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

/** DELETE /presale/{id}/delete — Удалить пресейл */
export const deletePresale = async (id: string): Promise<void> => {
  await apiReq(`presale/${id}/delete`, 'DELETE');
};

// ─── Files ────────────────────────────────────────────────────────────────────

/**
 * POST /process/context-document-parsing — Запуск парсинга контекстного документа.
 * Общая ручка: target_type определяет владельца (chat | scenario | project | presale).
 * Заменяет старую `POST /presale/{id}/files/attach`.
 */
export const processContextDocumentParsing = async (
  fileId: string,
  targetType: 'chat' | 'project' | 'scenario' | 'presale',
  targetId: string
): Promise<{ process_id: string }> => {
  const data = await apiReq<{ process_id: string }>('process/context-document-parsing', 'POST', {
    file_id: fileId,
    target_type: targetType,
    target_id: targetId,
  });
  if (!data) throw new Error('Empty response');
  return data;
};

// Backend использует POST для удаления (не DELETE) — согласно swagger
/** POST /presale/{id}/files/{docId}/delete — Soft-delete файла пресейла */
export const deletePresaleFile = async (presaleId: string, docId: string): Promise<void> => {
  await apiReq(`presale/${presaleId}/files/${docId}/delete`, 'POST');
};

// ─── Runs ─────────────────────────────────────────────────────────────────────

/** POST /presale/{id}/estimate — Запустить estimation-прогон */
export const runPresaleEstimate = async (id: string): Promise<PresaleRun> => {
  const data = await apiReq<PresaleRun>(`presale/${id}/estimate`, 'POST');
  if (!data) throw new Error('Empty response');
  return data;
};

/** GET /presale/{id}/run/{runId}/view — Получить состояние прогона */
export const getPresaleRun = async (presaleId: string, runId: string): Promise<PresaleRun> => {
  const data = await apiReq<PresaleRun>(`presale/${presaleId}/run/${runId}/view`);
  if (!data) throw new Error('Run not found');
  return data;
};

/** GET /presale/{id}/runs[?kind=...] — Список прогонов пресейла, опционально с фильтром по kind */
export const getPresaleRuns = async (
  presaleId: string,
  opts?: { kind?: 'prefill' | 'estimation' }
): Promise<PresaleRun[]> => {
  const qs = opts?.kind ? `?${new URLSearchParams({ kind: opts.kind })}` : '';
  const data = await apiReq<{ count: number; items: PresaleRun[] }>(
    `presale/${presaleId}/runs${qs}`
  );
  return data?.items ?? [];
};

/** POST /presale/{id}/prefill — Запуск prefill-прогона LLM (заполнение Bitrix-формы) */
export const runPresalePrefill = async (id: string): Promise<PresaleRun> => {
  const data = await apiReq<PresaleRun>(`presale/${id}/prefill`, 'POST');
  if (!data) throw new Error('Empty response');
  return data;
};

// ─── Bitrix form ──────────────────────────────────────────────────────────────

/** GET /presale/bitrix/schema — Структура полей Bitrix-формы пресейла */
export const getPresaleBitrixSchema = async (): Promise<BitrixFieldMeta[]> => {
  const data = await apiReq<BitrixFieldMeta[]>('presale/bitrix/schema');
  return Array.isArray(data) ? data : [];
};

/** GET /presale/{id}/bitrix/values — Текущие значения формы пресейла */
export const getPresaleBitrixValues = async (id: string): Promise<PresaleBitrixValues> => {
  const data = await apiReq<PresaleBitrixValues>(`presale/${id}/bitrix/values`);
  return data ?? { source: null, fields: null, run_id: null };
};

/** POST /presale/{id}/bitrix/submit — Отправить значения формы в Bitrix */
export const submitPresaleBitrix = async (
  id: string,
  form: SubmitPresaleBitrixForm
): Promise<Presale> => {
  const data = await apiReq<Presale>(`presale/${id}/bitrix/submit`, 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

// ─── System prompts ───────────────────────────────────────────────────────────

/** GET /presale/prefill-prompt — Получить промпт prefill-прогона */
export const getPrefillPrompt = async (): Promise<PresalePrompt> => {
  const data = await apiReq<PresalePrompt>('presale/prefill-prompt');
  if (!data) throw new Error('Empty response');
  return data;
};

/** GET /presale/estimation-prompt — Получить промпт estimation-прогона */
export const getEstimationPrompt = async (): Promise<PresalePrompt> => {
  const data = await apiReq<PresalePrompt>('presale/estimation-prompt');
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /presale/prefill-prompt/update — Обновить кастомный промпт prefill */
export const updatePrefillPrompt = async (
  form: PresalePromptUpdateForm
): Promise<PresalePrompt> => {
  const data = await apiReq<PresalePrompt>('presale/prefill-prompt/update', 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /presale/estimation-prompt/update — Обновить кастомный промпт estimation */
export const updateEstimationPrompt = async (
  form: PresalePromptUpdateForm
): Promise<PresalePrompt> => {
  const data = await apiReq<PresalePrompt>('presale/estimation-prompt/update', 'POST', form);
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /presale/prefill-prompt/delete — Удалить кастомный промпт prefill */
export const deletePrefillPrompt = async (): Promise<PresalePrompt> => {
  const data = await apiReq<PresalePrompt>('presale/prefill-prompt/delete', 'POST');
  if (!data) throw new Error('Empty response');
  return data;
};

/** POST /presale/estimation-prompt/delete — Удалить кастомный промпт estimation */
export const deleteEstimationPrompt = async (): Promise<PresalePrompt> => {
  const data = await apiReq<PresalePrompt>('presale/estimation-prompt/delete', 'POST');
  if (!data) throw new Error('Empty response');
  return data;
};
